import { FC, KeyboardEvent, useEffect } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { NavCloseIcon } from 'assets/icons';
import { SidePanel } from 'components';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { OutletContextType } from 'types/baseTypes';
import { selectCommunicationsPayload } from '../communicationsSlice';
import { useCommunications } from '../CommunicationsContext';
import { COMMUNICATIONS_CTA_TYPES } from '../constants';
import { hasValidCommunicationDetails } from '../utils';
import CommunicationDetailsPage from './CommunicationDetailsPage';

const CommunicationDetailsPanel: FC = () => {
  const location = useLocation();
  const { handleSidePanelClose } = useOutletContext<OutletContextType>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const payload = useAppSelector(selectCommunicationsPayload);
  const { fullPage } = useCommunications();
  const details = fullPage?.details;
  const payloadReady = payload != null && payload.teamId === currentTeam?.id;
  const canShow = fullPage?.ctaType === COMMUNICATIONS_CTA_TYPES.DETAILS && hasValidCommunicationDetails(details);
  const title = details?.title?.trim() || 'Payment details';

  useEffect(() => {
    if (!payloadReady) return;
    if (!canShow) handleSidePanelClose();
  }, [payloadReady, canShow, handleSidePanelClose]);

  const handleCloseKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleSidePanelClose();
  };

  if (!canShow || !details) return null;

  return (
    <SidePanel isOpen={true} onClose={handleSidePanelClose} shouldAnimate={location.state?.shouldAnimate}>
      <div className="flex h-full w-96 flex-col">
        <div className="flex items-center gap-2 px-4 pt-5">
          <button
            type="button"
            className="cursor-pointer"
            aria-label="Close details"
            tabIndex={0}
            onClick={handleSidePanelClose}
            onKeyDown={handleCloseKeyDown}>
            <NavCloseIcon id="communication-details-close" />
          </button>
          <h1 className="text-17px font-semibold text-heading">{title}</h1>
        </div>
        <div className="customNormalScroll min-h-0 flex-1 overflow-auto px-6 pt-5">
          <CommunicationDetailsPage details={details} />
        </div>
      </div>
    </SidePanel>
  );
};

export default CommunicationDetailsPanel;
